/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ImageUrlGenerator } from "@tivocorp/data-domain-lib";

import { CarouselTemplate, OfferType } from "../../model/enums";
import type { TCarouselItem, TContentType } from "../../model/types";
import { PlaybackContentType } from "../../model/videoplayer/enums";
import type { IMetadata, IPlayable } from "../../model/videoplayer/interfaces";
import { AiringPreviewUtils } from "../../model/videoplayer/utils/AiringPreviewUtils";
import { getTilePixelDimensions } from "../utility/CarouselTemplateUtil";

// ── Helpers ──────────────────────────────────────────────────────────────────

function deriveOfferType(playbackType: string): OfferType {
  switch (playbackType) {
    case PlaybackContentType.LIVE:
      return OfferType.Live;
    case PlaybackContentType.NDVR_LIVE:
    case PlaybackContentType.NDVR_RECORDED:
      return OfferType.Recording;
    case PlaybackContentType.STARTOVER_BEGINNING:
    case PlaybackContentType.STARTOVER_RESUME:
    case PlaybackContentType.CATCHUP_BEGINNING:
    case PlaybackContentType.CATCHUP_RESUME:
      return OfferType.Socu;
    case PlaybackContentType.VOD:
      return OfferType.Vod;
    default:
      return OfferType.Live;
  }
}

function selectPreviewByOfferType(
  offerType: OfferType,
  playbackType: string,
  airingOffers: unknown,
  vodOfferPreview: unknown,
  offerPreview: unknown
): unknown {
  if (offerType === OfferType.Live) {
    return Array.isArray(airingOffers) && airingOffers.length > 0
      ? airingOffers[0]
      : undefined;
  }
  if (playbackType === PlaybackContentType.VOD) {
    return vodOfferPreview !== undefined && vodOfferPreview !== null
      ? vodOfferPreview
      : offerPreview;
  }
  return offerPreview;
}

/** Safely read a string field from an unknown record. Empty string if absent. */
function readString(obj: unknown, key: string): string {
  if (obj === null || typeof obj !== "object") return "";
  const value = (obj as Record<string, unknown>)[key];
  return typeof value === "string" ? value : "";
}

/**
 * Extracts the `digItemId` used to build the tile image URL.
 *
 * Source is chosen by offer type:
 *   - LIVE → `airingOffers[0].logging`
 *   - everything else → `offerPreview.contentPreview`
 *
 * Within the chosen source, prefers `contentId`, falling back to `collectionId`.
 * If the chosen source is empty, falls back to the other source using the
 * same contentId → collectionId preference.
 */
function extractDigItemId(
  offerType: OfferType,
  airingOffers: unknown,
  offerPreview: unknown
): string {
  if (offerType === OfferType.Live) {
    // Live source: airingOffers[0].logging
    const firstAiring: unknown = Array.isArray(airingOffers)
      ? airingOffers[0]
      : undefined;
    const airingLogging: unknown =
      firstAiring !== undefined
        ? (firstAiring as Record<string, unknown>)["logging"]
        : undefined;
    const fromAiring: string =
      readString(airingLogging, "contentId") ||
      readString(airingLogging, "collectionId");

    return fromAiring;
  } else {
    // Non-live source: offerPreview
    const fromPreview: string =
      readString(offerPreview, "contentId") ||
      readString(offerPreview, "collectionId");
    return fromPreview;
  }
}

function parseSeasonEpisode(str: string | null): {
  seasonNumber?: number;
  episodeNumber?: number;
} {
  if (str === null) return {};
  const match = str.match(/S(\d+)\s+E(\d+)/);
  if (match === null || match[1] === undefined || match[2] === undefined) {
    return {};
  }
  return {
    seasonNumber: parseInt(match[1], 10),
    episodeNumber: parseInt(match[2], 10)
  };
}

// ── Main entry point ──────────────────────────────────────────────────────────

export interface NowPlayingTileMapperInput {
  playable: IPlayable;
  metadata: IMetadata;
  imageHostBaseUrl: string;
}

/**
 * Builds a TCarouselItem for the NowPlayingTile row from current background
 * playback. Pure — no React, no Redux, no VPC access. All inputs are injected
 * so this can be unit-tested against fixtures.
 */
export function buildNowPlayingTileData(
  input: NowPlayingTileMapperInput
): TCarouselItem {
  const { playable, metadata, imageHostBaseUrl } = input;

  const playbackType = playable.playbackContentType;
  const offerType = deriveOfferType(playbackType);
  const preview = selectPreviewByOfferType(
    offerType,
    playbackType,
    metadata.airingOffers,
    metadata.vodOfferPreview,
    metadata.offerPreview
  );

  const airingUtils = new AiringPreviewUtils(
    metadata.airingOffers,
    preview as Record<string, unknown> | undefined
  );

  const { seasonNumber, episodeNumber } = parseSeasonEpisode(
    airingUtils.getSeasonEpisode()
  );

  // TODO: For SOCU and Recording, fetch metadata from offer preview or other
  // sources. Currently airingUtils is Live-specific. Consider reusing
  // mapFeedItemTypeToContentType logic from homescreenMappers.ts.
  const movieYear = airingUtils.getMovieYear();
  const episodeTitle = airingUtils.getEpisodeTitle();
  const programType = airingUtils.getProgramType();
  const tileTitle = airingUtils.getChannelTitleText();

  // ISO 8601 start/end times for the progress bar.
  const rawStartTime: string = airingUtils.getStartTime();
  const startTime: string | undefined =
    rawStartTime !== "" ? rawStartTime : undefined;

  let endTime: string | undefined;
  if (startTime !== undefined) {
    const endDate: Date = airingUtils.getEndTime();
    const endMs: number = endDate.getTime();
    if (!Number.isNaN(endMs)) {
      endTime = endDate.toISOString();
    }
  }

  const id: string =
    playable.offerId !== undefined && playable.offerId !== ""
      ? playable.offerId
      : "";

  let channelName: string = "";
  if (playable.channel !== undefined) {
    if (playable.channel.name !== undefined && playable.channel.name !== "") {
      channelName = playable.channel.name;
    } else if (
      playable.channel.callSign !== undefined &&
      playable.channel.callSign !== ""
    ) {
      channelName = playable.channel.callSign;
    }
  }

  const channelNumber =
    playable.channel !== undefined &&
    playable.channel.channelNumber !== undefined
      ? playable.channel.channelNumber
      : "";

  const digItemId = extractDigItemId(
    offerType,
    metadata.airingOffers,
    metadata.offerPreview
  );

  const imageUrlGenerator = new ImageUrlGenerator();
  const { width: tileWidth, height: tileHeight } = getTilePixelDimensions(
    CarouselTemplate.PIP_LANDSCAPE_HORIZONTAL
  );
  const imageType = imageUrlGenerator.getImageTypeFromFeedItemType(
    programType === "movie" ? "Movie" : "Episode"
  );
  const imageUrl: string =
    imageHostBaseUrl.length > 0 && digItemId.length > 0
      ? imageUrlGenerator.buildImageUrl(
          imageHostBaseUrl,
          digItemId,
          imageType,
          tileWidth,
          tileHeight
        )
      : "";

  return {
    id,
    title: tileTitle,
    description: "",
    imageUrl,
    contentType: programType as TContentType,
    categoryLabel: "",
    duration: 0,
    isLive: false,
    isAdult: false,
    offerType,
    channelName,
    channelNumber,
    movieYear: movieYear > 0 ? movieYear : undefined,
    episodeTitle: episodeTitle || undefined,
    seasonNumber,
    episodeNumber,
    startTime: startTime,
    endTime: endTime,
    ratingBadge: undefined
  };
}
